import { FormInput } from 'components/Form';
import { ProTable } from 'components/ProTable/ProTable';
import { TimedMessage } from 'components/TimedMessage';
import Title from 'components/Title';
import { ZButton } from 'components/ZButton';
import { format, parseISO } from 'date-fns';
import { ZIconType } from 'helpers/utility/iconMap';
import { ReportExportBody } from 'models/common/storeGroupMessag';
import { Report } from 'models/dbModels/reports/Report';
import { DefaultDateFilter, DefaultDateFilterPeriod, ReportColumnType } from 'models/functionModels/reportFunctions/Common';
import { SalesReportFunction } from 'models/functionModels/reportFunctions/SalesReportFunction';
import { StockReportFunction } from 'models/functionModels/reportFunctions/StockReportFunction';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { cleanReports, generateSalesReport, generateStockReport, getReport, updateReportField } from 'redux/slices/Reports';
import { getStores } from 'redux/slices/Stores';
import { RootState } from 'redux/store';
import { getExportStockReport, getNormalSalesReportExport } from 'services/reportservices';

export function StockReport() {
	const { reportId } = useParams();
	const [isNewReport, setIsNewReport] = useState<boolean>(true);
	const dispatch = useAppDispatch();
	const currentStoreGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);

	const stores = useAppSelector((state: RootState) => state.stores);
	const report = useAppSelector((state: RootState) => state.reports);
	const [selectedStore, setSelectedStore] = useState<number>(0);
	const [filter, setFilter] = useState<StockReportFunction | SalesReportFunction>({
		filterCriteria: undefined,
		overrideFilterDates: false,
		designReportColumns: [],
		topN: null,
		orderByColumn: null,
		orderByDesc: false,
		startDateTime: null,
		endDateTime: null,
		startTime: null,
		endTime: null,
		timePeriodType: null,

		defaultStartDate: null,
		defaultStartDateOffset: null,
		defaultStartDatePeriod: null,

		defaultEndDate: null,
		defaultEndDateOffset: null,
		defaultEndDatePeriod: null,

		groupByColumn: null,
		groupByDesc1: false,
		groupByColumn2: null,
		groupByDesc2: false,
		showTotalsOnly: false,
		showMostFrequentInGroupTotals: false,
		averageTimeTotals: false,

		storeIds: [],
		offset: null,
		limit: null,
	});
	const [startDate, setStartDate] = useState<any>();
	const [endDate, setEndDate] = useState<any>();
	const [exporting, setExporting] = useState(false);
	const [generating, setGenerating] = useState(false);

	const [generateButtonEnabled, setGenerateButtonEnabled] = useState<boolean>(false);
	const [generateButtonTooltip, setGenerateButtonTooltip] = useState<string>("");


	const [columns, setColumns] = useState([
		{
			Header: '',
			accessor: 'a',
			Cell: (row: any) => <>{row.value}</>,
		}
	])

	

	useEffect(() => {
		dispatch(cleanReports());
		setIsNewReport(reportId === "new");
		dispatch(getStores({ storeGroupId: currentStoreGroupId }));
	}, [])

	
	useEffect(() => {
		dispatch(cleanReports());
		if (typeof reportId !== 'undefined' && !Number.isNaN(parseInt(reportId || ''))) {
			setIsNewReport(false);
			dispatch(getReport({ ids: [parseInt(reportId || '')] })).then((res) => LoadColumns(res));
		}
	}, [reportId, currentStoreGroupId]);

	function LoadColumns(res: Report) {
		if (res.filter !== undefined && res.filter != '' && res.filter !== null) {
			let parsedFilter = JSON.parse(res.filter) as StockReportFunction;
			let newFilter = { ...parsedFilter };

			let newColumns: any = [];
			let prevColumnAccessor: {
				type: ReportColumnType,
				number: number
			}[] = [];
			(parsedFilter.designReportColumns || []).forEach((item) => {

				if (item.type && item.type !== null && item.name !== '') {

					let key: string = item.type?.toString() ?? "";

					let match = prevColumnAccessor.find(i => i.type === item.type);
					if (match) {
						match.number++;
						key = key + "_" + match.number;
					} else { //First run doesn't get a number, only second duplicates
						prevColumnAccessor.push({ type: item.type, number: 0 })
					}
					newColumns.push({
						Header: item.name,
						accessor: key,
					})
				}
			})
			setColumns(newColumns);

			const [start, end] = ApplyDatesToFilters(newFilter);
			handleStartDate((start && format(start, 'yyyy-MM-dd')) || '');
			handleEndDate((end && format(end, 'yyyy-MM-dd')) || '');

			setFilter(newFilter);
		}
	}

	useEffect(() => {
		GenerateReport();
	}, [report.pageIndex]);

	useEffect(() => {
		if (!selectedStore) {
			setGenerateButtonEnabled(false);
			setGenerateButtonTooltip("Please select a store to generate a report for");
		} else {
			setGenerateButtonEnabled(true);
			setGenerateButtonTooltip("");
		}
	}, [selectedStore]);


	function ApplyDatesToFilters(inFilter?: StockReportFunction | SalesReportFunction): [Date | null, Date | null] {

		inFilter = inFilter ?? filter;
		var today = new Date();

		let startDate: Date | null = null;
		let endDate: Date | null = null;

		let startDateCalc = GetDefaultTimeDetails(inFilter.defaultStartDate ?? '', today);
		let endDateCalc = GetDefaultTimeDetails(inFilter.defaultEndDate ?? '', today);

		startDate = startDateCalc.value;
		endDate = endDateCalc.value;

		return [startDate, endDate];
	}

	function GetDefaultTimeDetails(s: string, baseDateTime: Date): { value: Date | null, referenceTime: DefaultDateFilter | null, offset: number | null, timeUnit: DefaultDateFilterPeriod | null } {
		let referenceTime: DefaultDateFilter | null = null;
		let offset: number | null = null;
		let timeUnit: DefaultDateFilterPeriod | null = null;
		let datetime: Date | null = null;

		if (s != null) {

			//Try parse the stirng into a date
			let parsed = Date.parse(s);
			if (!Number.isNaN(parsed)) {
				datetime = new Date(parsed);

				return { value: datetime, offset: offset, referenceTime: referenceTime, timeUnit: timeUnit };
			}


			let dateParams = s.split('|');
			if (dateParams.length == 3) {
				let refTimeInt = parseInt(dateParams[0]);
				if (!Number.isNaN(refTimeInt)) {
					datetime = null;
					referenceTime = refTimeInt;

					if (referenceTime == DefaultDateFilter.TODAY) {
						datetime = baseDateTime;
					} else if (refTimeInt == DefaultDateFilter.START_OF_WEEK) {
						datetime = GetMonday(baseDateTime);
					} else if (refTimeInt == DefaultDateFilter.END_OF_WEEK) {
						datetime = GetSunday(baseDateTime);
					} else if (refTimeInt == DefaultDateFilter.START_OF_MONTH) {
						datetime = new Date(baseDateTime.getFullYear(), baseDateTime.getMonth(), 1);
					} else if (refTimeInt == DefaultDateFilter.END_OF_MONTH) {
						datetime = new Date(baseDateTime.getFullYear(), baseDateTime.getMonth() + 1, 0);
					} else if (refTimeInt == DefaultDateFilter.START_OF_YEAR) {
						datetime = new Date(baseDateTime.getFullYear(), 1, 1);
					} else if (refTimeInt == DefaultDateFilter.END_OF_YEAR) {
						datetime = new Date(baseDateTime.getFullYear(), 12, 31);
					} else if (refTimeInt == DefaultDateFilter.START_OF_FY) {
						datetime = new Date(baseDateTime.getFullYear(), 7, 1);
					} else if (refTimeInt == DefaultDateFilter.END_OF_FY) {
						datetime = new Date(baseDateTime.getFullYear(), 6, 30);
					}

					let offSetInt = parseInt(dateParams[1]);
					let timeUnitInt = parseInt(dateParams[2]);


					if (!Number.isNaN(offSetInt) && !Number.isNaN(timeUnitInt)) {
						offset = offSetInt;
						timeUnit = timeUnitInt;

						if (datetime !== null) {
							if (timeUnit == DefaultDateFilterPeriod.DAYS) {
								datetime.setDate(datetime.getDate() + offset);
							} else if (timeUnit == DefaultDateFilterPeriod.WEEKS) {
								datetime.setDate(datetime.getDate() + offset * 7);
							} else if (timeUnit == DefaultDateFilterPeriod.MONTHS) {
								datetime.setMonth(datetime.getMonth() + offset);
							} else if (timeUnit == DefaultDateFilterPeriod.YEARS) {
								datetime.setFullYear(datetime.getFullYear() + offset);
							}
						}
					}
				}
			}
		}
		return { value: datetime, offset: offset, referenceTime: referenceTime, timeUnit: timeUnit };
	}


	function GetMonday(date: Date) {
		var day = date.getDay();
		var diff = date.getDate() - day + (day === 0 ? -6 : 1);
		return new Date(date.setDate(diff));
	}

	function GetSunday(date: Date) {
		var day = date.getDay();
		var diff = date.getDate() - day;
		return new Date(date.setDate(diff));
	}

	const GenerateButton =
		<div>
			<Button className="ms-1 cta-button"
				disabled={!generateButtonEnabled || generating || exporting}
				onClick={() => { GenerateReport(0) }}>
				{generating ? <Spinner className="spinner-border-sm me-1" color="white" /> : <></>}
				{generating ? <>Generating...</> : <>Generate</>}
			</Button>
		</div>;

	const GenerateButtonWithTooltip =
		<OverlayTrigger placement="top" overlay={
			<Tooltip id="tooltip-bottom" style={{ position: "fixed" }}>
				{generateButtonTooltip}
			</Tooltip>}
		>
			{GenerateButton}
		</OverlayTrigger>;


	async function GenerateReport(pageIndex: number | undefined = undefined, term: string | undefined = undefined) {
		if (selectedStore) {
			dispatch(updateReportField({ key: 'error', value: "" }));

			if (pageIndex !== undefined) {
				await dispatch(updateReportField({ key: 'pageIndex', value: pageIndex }));
			}

			setGenerating(true);

			let sendFilter = { ...filter, offset: (pageIndex ?? report.pageIndex) * 100 };
			sendFilter.limit = 100;
			sendFilter.storeIds = [selectedStore];
			sendFilter.startDateTime = startDate;
			sendFilter.endDateTime = endDate;



			if (report.reportType == 'Stock' || report.reportType === "StockExperimental") {
				dispatch(generateStockReport(sendFilter)).then((res) => {
					if (res.response?.data?.displayColumns) {
						let newColumns: any[] = [];
						Object.keys(res.response.data.displayColumns).forEach((item) => {
							newColumns.push({
								Header: res.response.data.displayColumns[item],
								accessor: item,
							})
						})
						setColumns(newColumns);
					}

					setGenerating(false);
				});
			} else if (report.reportType == 'Sales' || report.reportType === "SalesExperimental") {
				dispatch(generateSalesReport(sendFilter)).then((res) => {
					if (res.response?.data?.displayColumns) {
						let newColumns: any[] = [];
						Object.keys(res.response.data.displayColumns).forEach((item) => {
							newColumns.push({
								Header: res.response.data.displayColumns[item],
								accessor: item,
							})
						})
						setColumns(newColumns);
					}

					setGenerating(false);
				});
			}
		} else {
			dispatch(updateReportField({ key: 'error', value: "Store must be selected" }));
		}
	}

	async function ExportReport() {
		dispatch(updateReportField({ key: 'error', value: "" }));
		setExporting(true);

		let sendFilter = { ...filter, offset: null, limit: null };
		sendFilter.storeIds = [selectedStore];
		sendFilter.startDateTime = startDate;
		sendFilter.endDateTime = endDate;

		if (sendFilter.filterCriteria !== null) {
			let tempCriteria = { ...sendFilter.filterCriteria };

			tempCriteria.searchString = report.keyword;
			tempCriteria.containsSearch = true;

			tempCriteria.searchName = true;
			tempCriteria.searchBarcode = false;
			tempCriteria.searchPDE = false;


			sendFilter.filterCriteria = tempCriteria;
		} else {
			sendFilter.filterCriteria = {
				searchString: report.keyword,
				containsSearch: true,
				searchName: true,
				searchBarcode: false,
				searchPDE: false,
			}
		}
			
		if (report.reportType == 'Stock'|| report.reportType === "StockExperimental") {
			getExportStockReport(sendFilter, report.name, report.description);
			setExporting(false);			
		} else if (report.reportType == 'Sales'|| report.reportType === "SalesExperimental") {
			getNormalSalesReportExport(sendFilter, report.name, report.description);
			setExporting(false);
		}
	}

	function updateTableRows(dataTableState: any) {
		dispatch(updateReportField({ key: 'pageIndex', value: dataTableState.pageIndex }));
	}

	function onSearchClick(keyword: string) {
		dispatch(updateReportField({ key: 'keyword', value: keyword }));
		if (selectedStore) {
			GenerateReport(0, keyword);
		} else {
			dispatch(updateReportField({ key: 'error', value: "Store must be selected" }));
		}
	}

	function handleStartDate(value: any) {

		const newFilter = { ...filter };

		setStartDate(value);

		newFilter.filterCriteria?.advancedRangeFilters?.forEach((item) => {
			item.startDate = value && value !== '' ? value : null;
		});

		setFilter(newFilter);
	}

	function handleEndDate(value: any) {

		const newFilter = { ...filter };

		setEndDate(value);

		newFilter.filterCriteria?.advancedRangeFilters?.forEach((item) => {
			item.endDate = value && value !== '' ? value : null;
		});

		setFilter(newFilter);
	}

	const TopbarChild = () => {
		return (
			<div style={{ display: 'flex' }}>
				<div style={{ flex: '1' }} />

				<Dropdown className='page-title-middle me-3'>
					<Dropdown.Toggle id="dropdown-basic" variant='primary' style={{ fontSize: `var(--bs-nav-link-font-size)` }}>
						{
							// @ts-ignore
							stores.table.find((i: any) => i.id === selectedStore)?.name || "Store"
						}
					</Dropdown.Toggle>

					<Dropdown.Menu style={{ width: '350px' }}>
						{stores.table.map((e: any, i: number) => (
							<Dropdown.Item
								key={e.id}
								id={e.id.toString()}
								disabled={!e.connected}
								checked={selectedStore == e.id}
								onClick={() => {
									setSelectedStore(e.id);
									dispatch(updateReportField({ key: 'table', value: [] }));
								}}
							>{e.name}</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
			</div>
		)
	}


	return (
		<>
			<Title pageTitle={report.name} crumbs={[{ name: "Reports", path: "/app/reports" }]} appendTitle={<ZButton icon={ZIconType.Edit} size="sm" iconSize="14px" className='btn-icon custom-btn-icon' />}/>
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col>
									<Row>
										<Col md={8}>
											<FormInput
												required
												label="Description"
												type="text"
												name="report-description"
												placeholder="Enter report description"
												containerClass={'mb-3'}
												value={report.description}
												key="text"
												readOnly={true}
											/>
										</Col>
										<Col md={2}>
											<FormInput
												max={endDate ?? undefined}
												label="Start Date"
												type="date"
												name="report-start-date"
												placeholder="Enter report start date"
												containerClass={'mb-3'}
												value={startDate}
												onChange={(e) => handleStartDate(e.target.value)}
											/>
										</Col>
										<Col md={2}>
											<FormInput
												min={startDate ?? undefined}
												label="End Date"
												type="date"
												name="report-end-date"
												placeholder="Enter report end date"
												containerClass={'mb-3'}
												value={endDate}
												onChange={(e) => handleEndDate(e.target.value)}
											/>
										</Col>
									</Row>
									<Row>
										<Col xs={12}>
											<ProTable
												pageSize={100}
												data={report}
												isSortable={true}
												pagination={true}
												isSearchable={true}
												disableExport={true}
												columns={columns}
												table={report.table}
												onSearchClick={onSearchClick}
												updateTableRows={updateTableRows}
												keywordChange={(k) => console.log(k)}
												// saveSelectedRows={(r) => console.log(r)}
												topbarChild={<TopbarChild />}
											/>
										</Col>
									</Row>
									<Row className='mt-3'>
										<Col sm={8}>
											<Link to="/app/reports" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
												<i className="mdi mdi-arrow-left"></i> Back to Reports {' '}
											</Link>
										</Col>
										<Col xs={4} className="d-flex flex-reverse-row justify-content-end">											
											<Button className="ms-1 cta-button" variant="light" disabled={generating || exporting} onClick={ExportReport} >
												Export
											</Button>
											{generateButtonEnabled ? GenerateButton : GenerateButtonWithTooltip}
										</Col>
									</Row>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}